import { useCallback, useEffect, useRef, useState } from 'react';
import { preSessionReflectionApi } from '../../../services/api/preSessionReflection';

export type CommitmentReflectionAutosaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced autosave for the "How do you think things went?" reflection on a
 * prior session's commitment (MED-937 / E3.1b). Mirrors the cadence and UX
 * states of the main reflection autosave so the save-hint copy matches —
 * "Saving…" / "All changes saved." / "Save failed — try again." A single
 * debounce timer is enough here because there's only one field on the card.
 */
export function usePriorCommitmentReflectionAutosave(
  sessionId: string | undefined,
  debounceMs: number = 800,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<CommitmentReflectionAutosaveState>('idle');

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const flushNow = useCallback(
    async (reflectionText: string) => {
      if (!sessionId) return;
      try {
        setState('saving');
        await preSessionReflectionApi.upsertPriorCommitmentReflection(sessionId, reflectionText);
        setState('saved');
      } catch {
        setState('error');
      }
    },
    [sessionId],
  );

  const scheduleSave = useCallback(
    (reflectionText: string) => {
      if (!sessionId) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void flushNow(reflectionText);
      }, debounceMs);
    },
    [sessionId, debounceMs, flushNow],
  );

  return { state, scheduleSave };
}
